package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

object EntityViewStyles : StyleSheet() {
    val entityView by style {
        type("H1") style {
            fontWeight("normal")
        }
    }

    val titleArea by style {
        marginTop(5.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.FlexStart)
        marginBottom(20.px)
    }

    val subHead by style {
        marginTop(5.px)
    }
}

enum class EntityViewType {
    NORMAL,
    DIALOG
}

/**
 * Builder class for the entity view
 */
open class EntityViewConfiguration {
    var type: EntityViewType = EntityViewType.NORMAL
    var title: String? = null
    var subTitle: String? = null
    internal var content: (@Composable () -> Unit)? = null
    internal var breadcrumbConfig: (BreadcrumbConfiguration.() -> Unit)? = null
    internal val actions = mutableListOf<ButtonConfiguration>()
    fun breadcrumbs(config: BreadcrumbConfiguration.() -> Unit) {
        breadcrumbConfig = config
    }

    fun content(block: @Composable () -> Unit) {
        content = block
    }

    fun action(block: ButtonConfiguration.() -> Unit) {
        actions += ButtonConfiguration().apply(block)
    }

    fun action(title: String, primary: Boolean = false, impl: suspend () -> Unit) {
        actions += ButtonConfiguration(title, primary, impl)
    }
}

/**
 * Add a standard menu to the entity view.
 */
fun EntityViewConfiguration.menu(block: ButtonConfiguration.() -> Unit) {
    this.action {
        this.round = true
        content {
            svgIcon(SVGIcons.dots) {
                size = 20.px
            }
        }
        popupAlign = PopupAlign.Right
        block()
    }
}

@Composable
fun entityView(config: EntityViewConfiguration) {
    Div({ classes(EntityViewStyles.entityView) }) {
        if (config.title == null) {
            spinner()
        } else {
            if (config.breadcrumbConfig != null) {
                breadcrumbs(null, config.breadcrumbConfig!!)
            }

            config.title?.ifBlank { null }?.let { title ->
                Div({ classes(EntityViewStyles.titleArea) }) {
                    Div {
                        H1 {
                            Text(title)
                        }
                        config.subTitle?.let {
                            Div({ classes(EntityViewStyles.subHead) }) {
                                Text(it)
                            }
                        }
                    }

                    if (config.type == EntityViewType.NORMAL && config.actions.isNotEmpty()) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                gap(10.px)
                            }
                        }) {
                            config.actions.forEach {
                                button(it)
                            }
                        }
                    }
                }
            }

            config.content?.let { content ->
                Div {
                    content()
                }
            }

            if (config.type == EntityViewType.DIALOG && config.actions.isNotEmpty()) {
                Div({
                    style {
                        // The action row owns the gap above it, since content (e.g. a Box) has no
                        // outer margin. Collapses with a trailing field's bottom margin.
                        marginTop(20.px)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        gap(10.px)
                    }
                }) {
                    config.actions.forEach {
                        it.size = ButtonSize.LARGE
                        button(it)
                    }
                }
            }

        }
    }
}

/**
 * Renders a view of an entity. This generally includes a title, some actions and quite a bit
 * of content.
 */
@Composable
fun entityView(builder: EntityViewConfiguration.() -> Unit, content: @Composable () -> Unit) {
    val config = EntityViewConfiguration().apply(builder)
    config.content = content
    entityView(config)
}